import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PageShell from "../../components/dashboard/PageShell";
import { USE_MOCK_AUTH } from "../../config/api";
import { formatCurrency } from "../../data/mock/dashboardData";
import { fetchAssignmentTargets } from "../../services/institutionWorkspaceApi";
import { showError } from "../../utils/toast";

const RATING_CLASS = {
  excellent: "bg-emerald-100 text-emerald-800",
  good: "bg-sky-100 text-sky-800",
  fair: "bg-amber-100 text-amber-900",
  behind: "bg-orange-100 text-orange-800",
  poor: "bg-rose-100 text-rose-800",
};

function Rating({ value }) {
  const key = String(value || "poor").toLowerCase();
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold capitalize ${RATING_CLASS[key] || RATING_CLASS.poor}`}>
      {key}
    </span>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg bg-primary/[0.03] px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">{label}</p>
      <p className="mt-1 text-sm font-semibold text-primary">{value}</p>
    </div>
  );
}

export default function RecoveryTargetsPage() {
  const token = useSelector((state) => state.auth.token);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (USE_MOCK_AUTH || !token) {
      setRows([]);
      setLoading(false);
      return undefined;
    }
    fetchAssignmentTargets(token)
      .then((batches) => {
        if (active) setRows(Array.isArray(batches) ? batches : []);
      })
      .catch((error) => {
        if (active) showError(error.message || "Could not load recovery targets.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  return (
    <PageShell
      title="Recovery targets"
      description="Each assignment is a batch. Recovered payments inside the window are scored against the target set when the cases were assigned."
    >
      {loading ? (
        <p className="text-sm text-primary/45">Loading targets…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-primary/45">No assignment batches yet.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <article
              key={row.id || row.batch_code}
              className="rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)]"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-primary/45">{row.batch_code}</p>
                  <p className="mt-1 text-sm font-bold text-primary">
                    Call centre agent: {row.agent || "—"}
                  </p>
                  <p className="text-xs text-primary/55">
                    Call centre manager: {row.manager || "—"} · {row.case_count ?? 0} cases
                  </p>
                </div>
                <Rating value={row.rating} />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                <Metric label="Outstanding" value={formatCurrency(row.book_outstanding)} />
                <Metric label="Target" value={`${row.target_percent ?? 0}%`} />
                <Metric label="Recovered" value={formatCurrency(row.recovered)} />
                <Metric label="Hit" value={`${row.achievement ?? 0}%`} />
              </div>
              <p className="mt-3 text-xs text-primary/50">
                {row.frequency || "—"} · {row.window || "—"}
              </p>
            </article>
          ))}
        </div>
      )}
    </PageShell>
  );
}
